import type { Lang } from '@/i18n';

/**
 * The one-shot "explicit sign-in language choice" marker (slice 66q, Mahad 27 Sep).
 *
 * An EN/SO tap on the *pre-auth* sign-in screen wins over the account's saved
 * language AND is written to the account. This marker records that a tap happened.
 * It is read once — inside `useAuth.login()` — and cleared, so a stale tap can
 * never rewrite the next person's account on a shared device.
 *
 * 🔴 IN-MEMORY, NEVER PERSISTED. A persisted marker on a shared phone could
 * rewrite the *next* signer-in's account language. The trade-off — a full page
 * reload before signing in forgets the tap — is accepted (the account language is
 * then adopted, exactly as today). Module state is reset on every page load, so
 * bootstrap always sees a null marker on a fresh load; the safety test drives the
 * bootstrap path with the marker artificially set and asserts it is never read.
 */
let choice: Lang | null = null;

/** Record a pre-auth EN/SO tap. Called ONLY from `LangToggle`'s pre-auth branch. */
export function setSigninLangChoice(lang: Lang): void {
  choice = lang;
}

/** The pending sign-in choice, or null. Read ONLY from `useAuth.login()`. */
export function getSigninLangChoice(): Lang | null {
  return choice;
}

/** Forget the marker — after it is consumed, or on sign-out / forced logout. */
export function clearSigninLangChoice(): void {
  choice = null;
}
